import React, {Dispatch, SetStateAction, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import Button from '../../../../incase-domain/components/button/Button';
import {WizardContext, WIZARD_CONFIG} from '../steps';

type WizardStartOptionTitleKey =
    | 'caseWizard.initial.previewTitle'
    | 'caseWizard.initial.onDemandTitle'
    | 'caseWizard.initial.liveEventTitle';

type WizardStartOptionSubtitleKey =
    | 'caseWizard.initial.previewDescription'
    | 'caseWizard.initial.onDemandDescription'
    | 'caseWizard.initial.liveEventDescription';

interface RunOptionConfig {
    value: string;
    icon: string;
    titleKey: WizardStartOptionTitleKey;
    subtitleKey: WizardStartOptionSubtitleKey;
}

interface BaseWizardStartStepProps {
    wizardContext: WizardContext;
    setWizardContext: Dispatch<SetStateAction<WizardContext>>;
    selectedOption: string | null;
    onOptionSelect: (option: string) => void;
}

function BaseWizardStartStep({wizardContext, setWizardContext, selectedOption, onOptionSelect}: BaseWizardStartStepProps): JSX.Element {
    const {t} = useTranslation();

    const stepOptions = wizardContext.currentStep.options;

    const availableModes = new Set(Object.values(stepOptions));
    const OPTION_DEFS: RunOptionConfig[] = [
        {
            value: WIZARD_CONFIG.RUN_CASE.options.PREVIEW,
            icon: 'solar:play-linear',
            titleKey: 'caseWizard.initial.previewTitle',
            subtitleKey: 'caseWizard.initial.previewDescription',
        },
        {
            value: WIZARD_CONFIG.RUN_CASE.options.ON_DEMAND,
            icon: 'solar:plain-linear',
            titleKey: 'caseWizard.initial.onDemandTitle',
            subtitleKey: 'caseWizard.initial.onDemandDescription',
        },
        {
            value: WIZARD_CONFIG.RUN_CASE.options.LIVE_EVENT,
            icon: 'solar:users-group-rounded-linear',
            titleKey: 'caseWizard.initial.liveEventTitle',
            subtitleKey: 'caseWizard.initial.liveEventDescription',
        },
    ];

    const RUN_OPTIONS_CONFIG: RunOptionConfig[] = OPTION_DEFS.filter((opt) => availableModes.has(opt.value));

    const handleOptionSelect = useCallback(
        (selectedMode: string) => {
            if (onOptionSelect) {
                onOptionSelect(selectedMode);
                return;
            }

            let nextStep = wizardContext.currentStep;

            const modeToNextStep: Record<string, (typeof WIZARD_CONFIG)[keyof typeof WIZARD_CONFIG]> = {
                [WIZARD_CONFIG.RUN_CASE.options.ON_DEMAND]: WIZARD_CONFIG.ON_DEMAND,
                [WIZARD_CONFIG.RUN_CASE.options.LIVE_EVENT]: WIZARD_CONFIG.LIVE_EVENT,
            };

            const allowedModes = new Set(Object.values(stepOptions));
            if (allowedModes.has(selectedMode) && modeToNextStep[selectedMode]) {
                nextStep = modeToNextStep[selectedMode];
            }

            setWizardContext((prev) => ({
                ...prev,
                currentStep: nextStep,
            }));
        },
        [onOptionSelect, setWizardContext, wizardContext.currentStep, stepOptions]
    );

    const renderRunOption = useCallback(
        (option: RunOptionConfig) => {
            const isSelected = selectedOption === option.value;
            return (
                <Button
                    key={option.value}
                    onClick={() => handleOptionSelect(option.value)}
                    type="button"
                    color={selectedOption === option.value ? 'primary' : 'frost'}
                    variant={selectedOption === option.value ? 'outline' : 'ghost'}
                    leading={option.icon}
                    className={`run-option-button ${isSelected ? 'selected' : ''}`}
                    align="left"
                    fullWidth
                    size="xl"
                    disableHover={selectedOption === option.value}
                >
                    <div className="u-flex u-flex-col u-gap-1 u-ml-1">
                        <div className="c-case_wizard__content__title">{t(option.titleKey)}</div>
                        <div className="c-case_wizard__content__subtitle">{t(option.subtitleKey)}</div>
                    </div>
                </Button>
            );
        },
        [handleOptionSelect, t, selectedOption]
    );

    return <div>{RUN_OPTIONS_CONFIG.map(renderRunOption)}</div>;
}

export default BaseWizardStartStep;
